import React from "react";
import { useParams, Link } from "react-router-dom";
import BLOG_POSTS, { BLOG_AUTHOR } from "./blogData";
import "./BlogPost.css";
import Footer from "./Footer";

const BlogPost = () => {
  const { id } = useParams();
  const post = BLOG_POSTS.find((p) => p.id === id);
  const [body, setBody] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (post && post.title) {
      document.title = `Megavolt Electric | ${post.title}`;
    } else {
      document.title = "Megavolt Electric | Blog";
    }
  }, [post]);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${process.env.PUBLIC_URL || ""}/blogs/${id}.md`);
        if (!res.ok) throw new Error("not ok");
        const text = await res.text();
        if (!alive) return;
        // If dev server returned index.html fallback, ignore it
        const looksHtml = /<!DOCTYPE|<html[\s>]/i.test(text);
        setBody(looksHtml ? "" : text);
      } catch (_) {
        if (!alive) return;
        setBody("");
      } finally {
        if (alive) setLoaded(true);
      }
    };
    load();
    return () => { alive = false; };
  }, [id]);

  const renderContent = () => {
    if (!loaded) return <p>Loading...</p>;
    const text = body && body.trim().length > 0 ? body : post.excerpt;
    const lines = text.split(/\r?\n/);
    // If the markdown contains a title/date on first lines, skip them
    let startIdx = 0;
    if (lines.length >= 2 && lines[0].trim().length > 0 && /\d{4}/.test(lines[1])) {
      startIdx = 2;
    }
    const contentLines = lines.slice(startIdx);

    const elements = [];
    let para = [];
    const flushPara = () => {
      if (para.length) {
        elements.push(<p key={`p-${elements.length}`}>{para.join(" ")}</p>);
        para = [];
      }
    };
    contentLines.forEach((raw) => {
      const line = raw.trim();
      if (!line) {
        flushPara();
        return;
      }
      if (line.startsWith("### ")) {
        flushPara();
        elements.push(<h3 key={`h3-${elements.length}`}>{line.slice(4)}</h3>);
      } else if (line.startsWith("## ")) {
        flushPara();
        elements.push(<h2 key={`h2-${elements.length}`}>{line.slice(3)}</h2>);
      } else if (line.startsWith("# ")) {
        flushPara();
        elements.push(<h2 key={`h2-${elements.length}`}>{line.slice(2)}</h2>);
      } else {
        para.push(line);
      }
    });
    flushPara();
    return elements;
  };

  if (!post) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-back"><Link to="/blog">← Back to Blog</Link></div>
        <h1>Post not found</h1>
      </div>
    );
  }

  // For now, we only have excerpt text. If full content is added later,
  // render it here. The page is formatted and ready for longer content.
  const hasCover = Boolean(post.image);

  return (
    <div className={`blog-post-page ${hasCover ? "has-cover" : ""}`}>
      <div className="blog-post-back"><Link to="/blog">← Back to Blog</Link></div>
      <h1 className="blog-post-title">{post.title}</h1>
      <div className="blog-post-meta">{post.date} • By {BLOG_AUTHOR}</div>
      {hasCover && (
        <img className="blog-post-cover" src={post.image} alt={post.title} />
      )}
      <div className="blog-post-content">{renderContent()}</div>
      <Footer />
    </div>
  );
};

export default BlogPost;
